import { ContextData } from '@/core/ambient-intelligence/types/ambient-intelligence.types';

// Context extractor interface
export interface ContextExtractor {
  extract(pageData: any): ContextData;
  canExtract(pageType: string): boolean;
}

// Default context extractor
class DefaultContextExtractor implements ContextExtractor {
  extract(pageData: any): ContextData {
    return {
      type: 'general',
      confidence: 0.5,
      metadata: {
        url: pageData?.url || window.location.href,
        timestamp: new Date().toISOString(),
      },
    };
  }

  canExtract(): boolean {
    return true;
  }
}

/**
 * ContextRegistry - Extracts context from different page types
 * Enables understanding of any page context without hardcoding
 */
class ContextRegistryClass {
  private extractors = new Map<string, ContextExtractor>();
  private defaultExtractor = new DefaultContextExtractor();

  /**
   * Register a new context extractor
   */
  register(pageType: string, extractor: ContextExtractor): void {
    this.extractors.set(pageType, extractor);
    console.log(`✅ Registered context extractor: ${pageType}`);
  }

  /**
   * Extract context from current page
   */
  extractContext(pageType: string, pageData: any): ContextData {
    const extractor = this.extractors.get(pageType) || this.defaultExtractor;

    // Verify extractor can handle this page type
    if (!extractor.canExtract(pageType)) {
      console.warn(`Extractor cannot process page type: ${pageType}, using default`);
      return this.defaultExtractor.extract(pageData);
    }

    try {
      const context = extractor.extract(pageData);
      console.log(`✓ Context extracted for: ${pageType}`, context);
      return context;
    } catch (error) {
      console.error(`Context extraction failed for: ${pageType}`, error);
      return this.defaultExtractor.extract(pageData);
    }
  }

  /**
   * Get the best extractor for a page type
   */
  getExtractor(pageType: string): ContextExtractor {
    return this.extractors.get(pageType) || this.defaultExtractor;
  }

  /**
   * List all registered page types
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.extractors.keys());
  }

  /**
   * Check if a page type has a registered extractor
   */
  hasExtractor(pageType: string): boolean {
    return this.extractors.has(pageType);
  }

  /**
   * Analyze current page and determine its type
   */
  detectPageType(pageData?: any): string {
    // Check URL patterns
    const url = pageData?.url || window.location.pathname;

    if (url.includes('/product/') || url.includes('/products/')) {
      return 'product_detail';
    }
    if (url.includes('/blog/') || url.includes('/article/')) {
      return 'blog_detail';
    }
    if (url.includes('/gallery/') || url.includes('/images/')) {
      return 'image_gallery';
    }
    if (url === '/' || url.includes('/home')) {
      return 'home';
    }

    // Check page data for hints
    if (pageData?.type) {
      return pageData.type;
    }

    return 'general';
  }

  /**
   * Unregister a page type
   */
  unregister(pageType: string): void {
    this.extractors.delete(pageType);
  }

  /**
   * Clear all registrations
   */
  clear(): void {
    this.extractors.clear();
  }
}

// Export singleton instance
export const ContextRegistry = new ContextRegistryClass();

// Export types
export type { ContextExtractor };
